/**
 * Approvals page — pending approval queue with full mobile support.
 * Wraps PendingApprovals + order history in a clean page layout.
 *
 * v0.5.3 (AE-041): the recent-orders table now surfaces an explicit
 * "Cancel" button for orders that are still live at the broker
 * (status in {pending_approval, approved, partial}). Confirms before
 * calling `api.orders.cancel(clientOrderId)`.
 */
import { useEffect, useState } from 'react';
import { PendingApprovals } from '../PendingApprovals';
import { usePending } from '../../lib/usePending';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { PageHeader, PAGE_MAX_W } from '../PageHeader';
import * as api from '../../lib/api';

const CANCELABLE_STATUSES = new Set([
  'pending_approval',
  'approved',
  'partial',
]);

export function ApprovalsPage() {
  const { count, refresh: refreshPending } = usePending();
  const confirm = useConfirm();
  const toast = useToast();
  const [recentOrders, setRecentOrders] = useState<api.OrderPublic[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [canceling, setCanceling] = useState<Set<string>>(new Set());

  const fetchHistory = () => {
    // v0.5.6 AE-036: reconcile working LIVE orders against the broker's
    // actual state before rendering history, so a fill that happened
    // after placement shows as "filled" instead of a stale "approved".
    api.orders
      .refresh()
      .catch(() => undefined)
      .then(() => api.orders.list())
      .then((r) => setRecentOrders(r.orders))
      .catch(() => setRecentOrders([]));
  };

  useEffect(() => {
    if (showHistory) fetchHistory();
  }, [showHistory]);

  async function cancelOrder(o: api.OrderPublic) {
    const coid = o.client_order_id ?? o.id;
    if (!coid) return;
    const ok = await confirm(
      `Cancel ${o.side.toUpperCase()} ${o.quantity} ${o.symbol} (${o.mode.toUpperCase()})?\n\n` +
        `This sends a cancel request to the broker; already-filled orders cannot be canceled.`,
      { danger: true, confirmLabel: 'Cancel order', cancelLabel: 'Keep open' },
    );
    if (!ok) return;
    setCanceling((prev) => new Set(prev).add(coid));
    try {
      const res = await api.orders.cancel(coid);
      if (res.canceled) {
        toast.show(`Canceled ${o.symbol}`, 'ok');
      } else {
        toast.show(`${o.symbol} could not be canceled (likely already filled)`, 'warn');
      }
      fetchHistory();
      refreshPending();
    } catch (e: unknown) {
      toast.show((e as Error).message ?? 'cancel failed', 'bad');
    } finally {
      setCanceling((prev) => {
        const next = new Set(prev);
        next.delete(coid);
        return next;
      });
    }
  }

  return (
    <div className={`${PAGE_MAX_W} mx-auto space-y-4`}>
      <PageHeader
        title="Approvals"
        subtitle={
          count > 0 ? (
            <span><span className="text-warn font-semibold">{count}</span> pending — review and approve, defer, or cancel.</span>
          ) : (
            <span>Pending approval queue + recent order history.</span>
          )
        }
        actions={
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="btn text-xs"
            data-testid="toggle-history"
          >
            {showHistory ? 'hide history' : 'show order history'}
          </button>
        }
      />

      <PendingApprovals />

      {showHistory && recentOrders.length > 0 && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold mb-3">Recent orders</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs num">
              <thead className="text-ink-mute text-[11px] uppercase">
                <tr>
                  <th className="text-left py-1">when</th>
                  <th className="text-left py-1">symbol</th>
                  <th className="text-left py-1">side</th>
                  <th className="text-right py-1">qty</th>
                  <th className="text-right py-1">fill</th>
                  <th className="text-left py-1">status</th>
                  <th className="text-left py-1">mode</th>
                  <th className="text-right py-1"></th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => {
                  const coid = o.client_order_id ?? o.id ?? '';
                  const isCanceling = canceling.has(coid);
                  const cancelable = CANCELABLE_STATUSES.has(o.status);
                  return (
                    <tr
                      key={coid || Math.random()}
                      className="border-t border-edge"
                    >
                      <td className="py-1">{o.created_at.split('T')[1]?.slice(0, 8)}</td>
                      <td className="font-semibold">{o.symbol}</td>
                      <td className={o.side === 'buy' ? 'text-ok' : 'text-bad'}>{o.side}</td>
                      <td className="text-right">{o.quantity}</td>
                      <td className="text-right">
                        {o.fill_price !== null ? `$${o.fill_price.toFixed(2)}` : '—'}
                      </td>
                      <td>{o.status}</td>
                      <td className="uppercase text-ink-mute">{o.mode}</td>
                      <td className="text-right">
                        {cancelable && (
                          <button
                            className="btn text-[10px]"
                            disabled={isCanceling}
                            onClick={() => cancelOrder(o)}
                            data-testid={`cancel-${coid}`}
                          >
                            {isCanceling ? '…' : 'cancel'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
